import React, { useCallback, useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Button, Input, Space, Table } from "antd";
import { Link } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSelector } from "react-redux";
import firebase from "../../config/firebase";
import vehicleService from "../../service/vehicle.service";
import {
  getShipperScopeUids,
  recordMatchesShipperScope,
} from "../../util/shipper/applyShipperScopeParams";

const DataVehicleTable = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [datas, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);
  const user = useSelector((state) => state.user);

  const canView =
    user?.permissoes?.vehicle?.isView ||
    user?.permissoes?.veiculo?.visualizar;

  const canEdit =
    user?.permissoes?.vehicle?.isEdit ||
    user?.permissoes?.veiculo?.editar;

  const canDelete =
    user?.permissoes?.vehicle?.isDelete ||
    user?.permissoes?.veiculo?.excluir;

  const fetchData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const allowedUids = getShipperScopeUids(user);
      const collectionRef = firebase.firestore().collection("vehicle");

      const mapVehicleRow = (doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          vehiclePlate: data.vehiclePlate,
          descricaoVeiculo: data.descricaoVeiculo,
          vehicleType: data.vehicleType,
          ufVeiculo: data.ufVeiculo,
          uidShipper: data.uidShipper,
          reboquesCount: Array.isArray(data.reboques) ? data.reboques.length : 0,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        };
      };

      let items = [];

      if (allowedUids === null) {
        const snapshot = await collectionRef.limit(100).get();
        items = snapshot.docs.map(mapVehicleRow);
      } else if (allowedUids.length === 0) {
        items = [];
      } else if (allowedUids.length === 1) {
        const snapshot = await collectionRef
          .where("uidShipper", "==", allowedUids[0])
          .get();
        items = snapshot.docs.map(mapVehicleRow);
      } else {
        const chunks = [];
        for (let i = 0; i < allowedUids.length; i += 10) {
          chunks.push(allowedUids.slice(i, i + 10));
        }

        const snapshots = await Promise.all(
          chunks.map((uids) =>
            collectionRef.where("uidShipper", "in", uids).get(),
          ),
        );

        const byId = new Map();
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((doc) => {
            byId.set(doc.id, mapVehicleRow(doc));
          });
        });

        items = Array.from(byId.values());
      }

      items = items
        .filter((item) => recordMatchesShipperScope(item, user))
        .sort((a, b) => {
          const aTime = a.createdAt?.seconds ?? 0;
          const bTime = b.createdAt?.seconds ?? 0;
          return bTime - aTime;
        })
        .slice(0, 100);

      setData(items);
    } catch (error) {
      console.error("Erro ao buscar veículos:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [user?.uid, user?.uidShipper, user?.viewALL, fetchData]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const deleteVehicle = async (id) => {
    const result = window.confirm("Deseja mesmo excluir este veículo?");
    if (!result) return;

    try {
      await vehicleService.delete(id);
      alert("Veículo excluído com sucesso!");
      fetchData();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Não foi possível excluir este veículo.";
      alert(message);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Limpar
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Fechar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Placa",
      dataIndex: "vehiclePlate",
      key: "vehiclePlate",
      ...getColumnSearchProps("vehiclePlate"),
      sorter: (a, b) =>
        (a.vehiclePlate || "").localeCompare(b.vehiclePlate || ""),
    },
    {
      title: "Descrição",
      dataIndex: "descricaoVeiculo",
      key: "descricaoVeiculo",
      ...getColumnSearchProps("descricaoVeiculo"),
      sorter: (a, b) =>
        (a.descricaoVeiculo || "").localeCompare(b.descricaoVeiculo || ""),
    },
    {
      title: "Tipo",
      dataIndex: "vehicleType",
      key: "vehicleType",
      ...getColumnSearchProps("vehicleType"),
      sorter: (a, b) =>
        (a.vehicleType || "").localeCompare(b.vehicleType || ""),
    },
    {
      title: "UF",
      dataIndex: "ufVeiculo",
      key: "ufVeiculo",
      ...getColumnSearchProps("ufVeiculo"),
    },
    {
      title: "Reboques",
      dataIndex: "reboquesCount",
      key: "reboquesCount",
      sorter: (a, b) => (a.reboquesCount || 0) - (b.reboquesCount || 0),
    },
    {
      title: "Ações",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Space size="middle">
          {canEdit ? (
            <Link to={`/vehicle/${record.id}`}>
              <EditOutlinedIcon />
            </Link>
          ) : canView ? (
            <Link to={`/vehicle/${record.id}`} state={{ viewOnly: true }}>
              <VisibilityIcon />
            </Link>
          ) : null}
          {canDelete ? (
            <Link to="#">
              <DeleteOutlinedIcon
                onClick={() => deleteVehicle(record.id)}
                color="error"
              />
            </Link>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={datas}
      loading={loading}
    />
  );
};

export default DataVehicleTable;
